import { ICON_COLORS } from "@/domains/visualization/constants/graph"

export interface StylingContext {
  svgElement: SVGSVGElement
  color: string
  primaryColor?: string
  secondaryColor?: string
}

export const applyLineIconStyling = ({ svgElement, color }: StylingContext) => {
  // For line icons, set stroke properties and ensure no fill
  svgElement.style.color = color
  svgElement.style.stroke = color

  const allPaths = svgElement.querySelectorAll("path")

  allPaths.forEach((path) => {
    path.style.stroke = color
    path.style.fill = "none"
    // Remove any existing fill attribute that might override the style
    if (path.getAttribute("fill")) {
      path.removeAttribute("fill")
    }
  })
}

export const iconStylingStrategies = {
  fill: ({ svgElement, primaryColor, secondaryColor }: StylingContext) => {
    // Fill icons use specific classes for primary/secondary paths
    const primaryPaths = svgElement.querySelectorAll(".FrIcon--fill-primary")
    const secondaryPaths = svgElement.querySelectorAll(".FrIcon--fill-secondary")

    primaryPaths.forEach((path) => {
      ;(path as SVGPathElement).style.fill = primaryColor ?? ICON_COLORS.PRIMARY
    })

    secondaryPaths.forEach((path) => {
      ;(path as SVGPathElement).style.fill = secondaryColor ?? ICON_COLORS.SECONDARY
    })
  },

  twoTone: ({ svgElement, primaryColor, secondaryColor }: StylingContext) => {
    // TwoTone icons: first path is fill (primary), paths with stroke="currentColor" get secondary
    const allPaths = svgElement.querySelectorAll("path")

    allPaths.forEach((path) => {
      const hasStroke = path.getAttribute("stroke") === "currentColor"
      if (hasStroke) {
        path.style.stroke = secondaryColor ?? ICON_COLORS.SECONDARY
      } else {
        path.style.fill = primaryColor ?? ICON_COLORS.PRIMARY
      }
    })
  },

  line: applyLineIconStyling,
  // Fallback: treat unknown icons as line icons
  default: applyLineIconStyling
} as const

/**
 * Determines the icon type based on SVG CSS classes
 */
function determineIconType(svgClasses: string): keyof typeof iconStylingStrategies {
  if (svgClasses.includes("FrIcon--fill")) return "fill"
  if (svgClasses.includes("FrIcon--two-tone")) return "twoTone"
  if (svgClasses.includes("FrIcon--line")) return "line"
  return "default"
}

/**
 * Applies proper styling to SVG based on its actual rendered classes
 */
export function applySvgStyling(
  svgElement: SVGSVGElement,
  color: string,
  primaryColor?: string,
  secondaryColor?: string
): void {
  const svgClasses = svgElement.getAttribute("class") ?? ""
  const iconType = determineIconType(svgClasses)

  // Apply styling using the appropriate strategy
  const stylingContext: StylingContext = {
    svgElement,
    color,
    primaryColor,
    secondaryColor
  }

  iconStylingStrategies[iconType](stylingContext)
}

/**
 * Converts an SVG element to a Canvas element
 */
export async function svgToCanvas(svgElement: SVGSVGElement, size: number): Promise<HTMLCanvasElement> {
  // Clone the SVG to avoid modifying the original
  const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement

  // Get the computed color from the parent container
  const parentDiv = svgElement.parentElement
  const computedColor = parentDiv ? getComputedStyle(parentDiv).color : ICON_COLORS.DEFAULT

  // Ensure proper dimensions and styling
  clonedSvg.setAttribute("width", size.toString())
  clonedSvg.setAttribute("height", size.toString())

  // Detect icon type from SVG classes to apply correct styling
  const svgClasses = clonedSvg.getAttribute("class") ?? ""
  const isLineIcon = svgClasses.includes("FrIcon--line")

  // Apply color styling to all path elements based on icon type
  const paths = clonedSvg.querySelectorAll("path")
  paths.forEach((path) => {
    if (isLineIcon) {
      // For line icons, set stroke color on all paths and ensure fill is none
      path.setAttribute("stroke", computedColor)
      path.setAttribute("fill", "none")
      // Remove any existing fill attribute that might interfere
      if (path.getAttribute("fill") && path.getAttribute("fill") !== "none") {
        path.setAttribute("fill", "none")
      }
    } else {
      // For fill icons, set fill color
      if (!path.getAttribute("fill") || path.getAttribute("fill") === "currentColor") {
        path.setAttribute("fill", computedColor)
      }
    }
  })

  // Also set the color on the SVG itself
  clonedSvg.style.color = computedColor

  // For line icons, also set stroke on the SVG element
  if (isLineIcon) {
    clonedSvg.style.stroke = computedColor
    clonedSvg.setAttribute("stroke", computedColor)
  }

  // Convert SVG to data URL
  const svgData = new XMLSerializer().serializeToString(clonedSvg)
  const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" })
  const svgUrl = URL.createObjectURL(svgBlob)

  let img: HTMLImageElement | null = null
  let canvas: HTMLCanvasElement | null = null

  try {
    // Create an image from the SVG
    img = new Image()
    await new Promise<void>((resolve, reject) => {
      if (!img) {
        reject(new Error("Failed to create image"))
        return
      }

      img.onload = () => {
        resolve()
      }
      img.onerror = reject
      img.src = svgUrl
    })

    // Draw the image to a canvas
    canvas = document.createElement("canvas")
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext("2d")

    if (!ctx) {
      throw new Error("Failed to get canvas 2D context")
    }

    // Draw the image to fill the entire canvas
    ctx.drawImage(img, 0, 0, size, size)

    return canvas
  } catch (error) {
    // Clean up canvas on error
    if (canvas) {
      const ctx = canvas.getContext("2d")
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
      canvas.width = 0
      canvas.height = 0
    }
    throw error
  } finally {
    // Always cleanup resources
    URL.revokeObjectURL(svgUrl)
    if (img) {
      img.onload = null
      img.onerror = null
      img.src = ""
    }
  }
}
